import { localAIService } from './local-ai-service'
import { deepseekOptimizedService } from './deepseek-optimized-service'

export type AIMode = 'local' | 'cloud' | 'auto'

export interface HybridAIConfig {
  defaultMode: AIMode
  fallbackToLocal: boolean
  cloudTimeout: number
  retryCount: number
  retryDelay: number
  enableAutoSwitch: boolean
  enableCache: boolean
  cacheTTL: number
}

export interface AIResponse {
  text: string
  mode: AIMode
  cached: boolean
  responseTime: number
  modelName: string
  intent?: string
  slots?: Record<string, any>
}

export interface ServiceStatus {
  mode: AIMode
  cloud: {
    available: boolean
    configured: boolean
    lastSuccess?: Date
    errorRate: number
    latency?: number
  }
  local: {
    available: boolean
    loaded: boolean
    loading: boolean
    modelName?: string
  }
  system: {
    activeSessions: number
    totalRequests: number
    cacheHitRate: number
  }
}

interface PerformanceStats {
  totalRequests: number
  cloudRequests: number
  localRequests: number
  cacheHits: number
  totalResponseTime: number
  errors: {
    count: number
    lastError?: Date
    lastErrorMessage?: string
    byType: Record<string, number>
  }
  latencies: {
    cloud: number[]
    local: number[]
  }
}

interface CacheEntry {
  key: string
  text: string
  mode: AIMode
  timestamp: number
  ttl: number
}

export class HybridAIService {
  private currentMode: AIMode
  private config: HybridAIConfig
  private cloudEnabled: boolean
  private performanceStats: PerformanceStats
  private cloudErrorHistory: Array<{ timestamp: number; error: string }>
  private loadProgressCallback: ((progress: number, status: string) => void) | null = null
  private lastAutoMode: AIMode = 'local'
  private responseCache: Map<string, CacheEntry> = new Map()
  private lastHealthCheck: number = 0
  private healthCheckInterval: number = 5 * 60 * 1000

  private static readonly DEFAULT_CONFIG: HybridAIConfig = {
    defaultMode: 'local',
    fallbackToLocal: true,
    cloudTimeout: 15000,
    retryCount: 3,
    retryDelay: 1000,
    enableAutoSwitch: false,
    enableCache: true,
    cacheTTL: 30 * 60 * 1000
  }

  constructor(config: Partial<HybridAIConfig> = {}) {
    this.config = { ...HybridAIService.DEFAULT_CONFIG, ...config }
    this.currentMode = this.config.defaultMode
    this.cloudEnabled = !!import.meta.env.VITE_DEEPSEEK_API_KEY && 
                      import.meta.env.VITE_DEEPSEEK_API_KEY !== 'sk-xxxxxxxx' &&
                      import.meta.env.VITE_DEEPSEEK_API_KEY !== 'your-api-key-here'
    this.performanceStats = {
      totalRequests: 0,
      cloudRequests: 0,
      localRequests: 0,
      cacheHits: 0,
      totalResponseTime: 0,
      errors: { 
        count: 0,
        byType: {}
      },
      latencies: {
        cloud: [],
        local: []
      }
    }
    this.cloudErrorHistory = []
    this.loadPersistentCache()
  }

  setMode(mode: AIMode): void {
    this.currentMode = mode
    this.lastAutoMode = mode
    console.log(`AI mode changed to: ${mode}`)
  }

  getMode(): AIMode {
    return this.currentMode
  }

  isCloudEnabled(): boolean {
    return this.cloudEnabled
  }

  setLoadProgressCallback(callback: (progress: number, status: string) => void): void {
    this.loadProgressCallback = callback
    localAIService.setLoadProgressCallback(callback)
  }

  private async determineMode(): Promise<AIMode> {
    if (this.currentMode !== 'auto') {
      if (this.currentMode === 'cloud' && !this.cloudEnabled) {
        return 'local'
      }
      return this.currentMode
    }

    return 'local'
  }

  private shouldUseCloud(): boolean {
    if (this.currentMode === 'local') return false
    if (!this.cloudEnabled) return false
    if (this.cloudErrorHistory.length === 0) return true
    
    const oneHourAgo = Date.now() - 60 * 60 * 1000
    const recentErrors = this.cloudErrorHistory.filter(e => e.timestamp > oneHourAgo)
    
    if (recentErrors.length >= 5) {
      console.warn('Cloud service has too many errors, preferring local')
      return false
    }
    
    const cloudLatency = this.getCloudAvgLatency()
    const localLatency = this.getLocalAvgLatency()
    
    if (cloudLatency > 0 && localLatency > 0 && cloudLatency > localLatency * 2) {
      console.warn('Cloud latency too high, preferring local')
      return false
    }
    
    return true
  }

  private getCloudAvgLatency(): number {
    const latencies = this.performanceStats.latencies.cloud
    if (latencies.length === 0) return 0
    return latencies.reduce((a, b) => a + b, 0) / latencies.length
  }

  private getLocalAvgLatency(): number {
    const latencies = this.performanceStats.latencies.local
    if (latencies.length === 0) return 0
    return latencies.reduce((a, b) => a + b, 0) / latencies.length
  }

  private async performHealthCheck(): Promise<void> {
    const now = Date.now()
    if (now - this.lastHealthCheck < this.healthCheckInterval) return
    
    this.lastHealthCheck = now
    
    try {
      if (this.cloudEnabled) {
        const startTime = Date.now()
        await this.testCloudService()
        const latency = Date.now() - startTime
        this.performanceStats.latencies.cloud.push(latency)
        if (this.performanceStats.latencies.cloud.length > 10) {
          this.performanceStats.latencies.cloud.shift()
        }
      }
    } catch {
      console.debug('Health check: cloud service unavailable')
    }
  }

  private async testCloudService(): Promise<void> {
    const testMessages = [
      { role: 'user', content: 'Hello' }
    ]
    await deepseekOptimizedService.chat(testMessages, { maxTokens: 10 })
  }

  private recordCloudError(error: Error): void {
    this.performanceStats.errors.count++
    this.performanceStats.errors.lastError = new Date()
    this.performanceStats.errors.lastErrorMessage = error.message
    
    const errorType = error.message.includes('timeout') ? 'timeout' : 
                      error.message.includes('network') ? 'network' : 'other'
    this.performanceStats.errors.byType[errorType] = (this.performanceStats.errors.byType[errorType] || 0) + 1
    
    this.cloudErrorHistory.push({
      timestamp: Date.now(),
      error: error.message
    })
    this.cloudErrorHistory = this.cloudErrorHistory.slice(-20)
  }

  private generateCacheKey(message: string, systemPrompt?: string): string {
    const key = JSON.stringify({ message, systemPrompt })
    let hash = 0
    for (let i = 0; i < key.length; i++) {
      const char = key.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return 'hybrid_' + Math.abs(hash).toString(36)
  }

  private getFromCache(key: string): CacheEntry | null {
    if (!this.config.enableCache) return null
    
    const entry = this.responseCache.get(key)
    if (entry && Date.now() - entry.timestamp < entry.ttl) {
      return entry
    }
    if (entry) {
      this.responseCache.delete(key)
    }
    return null
  }

  private addToCache(key: string, text: string, mode: AIMode): void {
    if (!this.config.enableCache) return
    
    if (this.responseCache.size >= 200) {
      const oldestKey = Array.from(this.responseCache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp)[0][0]
      this.responseCache.delete(oldestKey)
    }
    
    this.responseCache.set(key, {
      key,
      text,
      mode,
      timestamp: Date.now(),
      ttl: this.config.cacheTTL
    })
    
    this.savePersistentCache()
  }

  private loadPersistentCache(): void {
    try {
      const cached = localStorage.getItem('hybrid_ai_cache')
      if (cached) {
        const data = JSON.parse(cached)
        const now = Date.now()
        
        for (const [key, entry] of Object.entries(data)) {
          const cacheEntry = entry as CacheEntry
          if (now - cacheEntry.timestamp < cacheEntry.ttl) {
            this.responseCache.set(key, cacheEntry)
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load persistent cache:', error)
    }
  }

  private savePersistentCache(): void {
    try {
      const data = Object.fromEntries(this.responseCache.entries())
      localStorage.setItem('hybrid_ai_cache', JSON.stringify(data))
    } catch (error) {
      console.warn('Failed to save persistent cache:', error)
    }
  }

  async chat(message: string, systemPrompt?: string): Promise<AIResponse> {
    const startTime = Date.now()
    this.performanceStats.totalRequests++
    
    const cacheKey = this.generateCacheKey(message, systemPrompt)
    const cached = this.getFromCache(cacheKey)
    
    if (cached) {
      this.performanceStats.cacheHits++
      return {
        text: cached.text,
        mode: cached.mode,
        cached: true,
        responseTime: Date.now() - startTime,
        modelName: cached.mode === 'cloud' ? 'DeepSeek-V4-Flash' : 'Qwen2-1.5B'
      }
    }
    
    try {
      const mode = await this.determineMode()
      
      let response: AIResponse
      
      if (mode === 'cloud') {
        response = await this.chatWithCloud(message, systemPrompt, startTime)
        this.performanceStats.cloudRequests++
        this.performanceStats.latencies.cloud.push(response.responseTime)
        if (this.performanceStats.latencies.cloud.length > 10) {
          this.performanceStats.latencies.cloud.shift()
        }
      } else {
        response = await this.chatWithLocal(message, systemPrompt, startTime)
        this.performanceStats.localRequests++
        this.performanceStats.latencies.local.push(response.responseTime)
        if (this.performanceStats.latencies.local.length > 10) {
          this.performanceStats.latencies.local.shift()
        }
      }
      
      this.performanceStats.totalResponseTime += response.responseTime
      
      if (!response.cached) {
        this.addToCache(cacheKey, response.text, response.mode)
      }
      
      return response
    } catch (error) {
      console.error('Hybrid AI service failed:', error)
      
      if (this.config.fallbackToLocal) {
        console.warn('Falling back to local mode')
        const fallbackResponse = await this.chatWithLocal(message, systemPrompt, startTime)
        this.addToCache(cacheKey, fallbackResponse.text, 'local')
        return fallbackResponse
      }
      
      throw error
    }
  }

  private async chatWithCloud(message: string, systemPrompt?: string, startTime: number): Promise<AIResponse> {
    if (!this.cloudEnabled) {
      return this.chatWithLocal(message, systemPrompt, startTime)
    }
    try {
      const messages = [
        { role: 'system', content: systemPrompt || '你是谐伴AI助手，一个专业的后台管理系统智能助手。请使用中文回复。' },
        { role: 'user', content: message }
      ]
      
      const response = await Promise.race([
        deepseekOptimizedService.chat(messages),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Cloud timeout')), this.config.cloudTimeout))
      ])
      
      return {
        text: response.choices[0]?.message?.content || '',
        mode: 'cloud',
        cached: false,
        responseTime: Date.now() - startTime,
        modelName: 'DeepSeek-V4-Flash'
      }
    } catch (error) {
      this.recordCloudError(error as Error)
      if (this.config.fallbackToLocal) {
        return this.chatWithLocal(message, systemPrompt, startTime)
      }
      throw error
    }
  }

  private async chatWithLocal(message: string, systemPrompt?: string, startTime: number): Promise<AIResponse> {
    const localResponse = await localAIService.chat(message, systemPrompt)
    return {
      text: localResponse.text,
      mode: 'local',
      cached: false,
      responseTime: Date.now() - startTime,
      modelName: 'Qwen2-1.5B',
      intent: localResponse.intent,
      slots: localResponse.slots
    }
  }

  async analyzeData(data: any, prompt: string): Promise<AIResponse> {
    const startTime = Date.now()
    this.performanceStats.totalRequests++
    
    try {
      const mode = await this.determineMode()
      let response: AIResponse
      
      if (mode === 'cloud' && this.cloudEnabled) {
        const cloudResponse = await deepseekOptimizedService.analyzeData(data, prompt)
        response = {
          text: cloudResponse,
          mode: 'cloud',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'DeepSeek-V4-Flash'
        }
        this.performanceStats.cloudRequests++
      } else {
        const localResponse = await localAIService.analyzeData(data, prompt)
        response = {
          text: localResponse.text,
          mode: 'local',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'Qwen2-1.5B',
          intent: localResponse.intent,
          slots: localResponse.slots
        }
        this.performanceStats.localRequests++
      }
      
      this.performanceStats.totalResponseTime += (Date.now() - startTime)
      return response
    } catch (error) {
      console.error('Analysis failed:', error)
      this.recordCloudError(error as Error)
      
      const localResponse = await localAIService.analyzeData(data, prompt)
      return {
        text: localResponse.text,
        mode: 'local',
        cached: false,
        responseTime: Date.now() - startTime,
        modelName: 'Qwen2-1.5B',
        intent: localResponse.intent,
        slots: localResponse.slots
      }
    }
  }

  async generateReport(data: any, type: string = 'summary'): Promise<AIResponse> {
    const startTime = Date.now()
    this.performanceStats.totalRequests++
    
    try {
      const mode = await this.determineMode()
      let response: AIResponse
      
      if (mode === 'cloud' && this.cloudEnabled) {
        const cloudResponse = await deepseekOptimizedService.generateReport(data, type)
        response = {
          text: cloudResponse,
          mode: 'cloud',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'DeepSeek-V4-Flash'
        }
        this.performanceStats.cloudRequests++
      } else {
        const localResponse = await localAIService.generateReport(data, type)
        response = {
          text: localResponse.text,
          mode: 'local',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'Qwen2-1.5B',
          intent: localResponse.intent,
          slots: localResponse.slots
        }
        this.performanceStats.localRequests++
      }
      
      this.performanceStats.totalResponseTime += (Date.now() - startTime)
      return response
    } catch (error) {
      console.error('Report generation failed:', error)
      this.recordCloudError(error as Error)
      
      const localResponse = await localAIService.generateReport(data, type)
      return {
        text: localResponse.text,
        mode: 'local',
        cached: false,
        responseTime: Date.now() - startTime,
        modelName: 'Qwen2-1.5B',
        intent: localResponse.intent,
        slots: localResponse.slots
      }
    }
  }

  async summarizeText(text: string, maxLength: number = 300): Promise<AIResponse> {
    const startTime = Date.now()
    this.performanceStats.totalRequests++
    
    try {
      const mode = await this.determineMode()
      let response: AIResponse
      
      if (mode === 'cloud' && this.cloudEnabled) {
        const cloudResponse = await deepseekOptimizedService.summarizeText(text, maxLength)
        response = {
          text: cloudResponse,
          mode: 'cloud',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'DeepSeek-V4-Flash'
        }
        this.performanceStats.cloudRequests++
      } else {
        const localResponse = await localAIService.summarizeText(text, maxLength)
        response = {
          text: localResponse.text,
          mode: 'local',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'Qwen2-1.5B',
          intent: localResponse.intent,
          slots: localResponse.slots
        }
        this.performanceStats.localRequests++
      }
      
      this.performanceStats.totalResponseTime += (Date.now() - startTime)
      return response
    } catch (error) {
      console.error('Summary failed:', error)
      this.recordCloudError(error as Error)
      
      const localResponse = await localAIService.summarizeText(text, maxLength)
      return {
        text: localResponse.text,
        mode: 'local',
        cached: false,
        responseTime: Date.now() - startTime,
        modelName: 'Qwen2-1.5B',
        intent: localResponse.intent,
        slots: localResponse.slots
      }
    }
  }

  async extractKeywords(text: string, count: number = 10): Promise<AIResponse> {
    const startTime = Date.now()
    this.performanceStats.totalRequests++
    
    try {
      const mode = await this.determineMode()
      let response: AIResponse
      
      if (mode === 'cloud' && this.cloudEnabled) {
        const cloudResponse = await deepseekOptimizedService.extractKeywords(text, count)
        response = {
          text: Array.isArray(cloudResponse) ? cloudResponse.join(', ') : cloudResponse,
          mode: 'cloud',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'DeepSeek-V4-Flash'
        }
        this.performanceStats.cloudRequests++
      } else {
        const localResponse = await localAIService.extractKeywords(text, count)
        response = {
          text: localResponse.text,
          mode: 'local',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'Qwen2-1.5B',
          intent: localResponse.intent,
          slots: localResponse.slots
        }
        this.performanceStats.localRequests++
      }
      
      this.performanceStats.totalResponseTime += (Date.now() - startTime)
      return response
    } catch (error) {
      console.error('Keyword extraction failed:', error)
      this.recordCloudError(error as Error)
      
      const localResponse = await localAIService.extractKeywords(text, count)
      return {
        text: localResponse.text,
        mode: 'local',
        cached: false,
        responseTime: Date.now() - startTime,
        modelName: 'Qwen2-1.5B',
        intent: localResponse.intent,
        slots: localResponse.slots
      }
    }
  }

  async translateText(text: string, targetLang: string = '中文'): Promise<AIResponse> {
    const startTime = Date.now()
    this.performanceStats.totalRequests++
    
    try {
      const mode = await this.determineMode()
      let response: AIResponse
      
      if (mode === 'cloud' && this.cloudEnabled) {
        const cloudResponse = await deepseekOptimizedService.translateText(text, targetLang)
        response = {
          text: cloudResponse,
          mode: 'cloud',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'DeepSeek-V4-Flash'
        }
        this.performanceStats.cloudRequests++
      } else {
        const localResponse = await localAIService.translateText(text, targetLang)
        response = {
          text: localResponse.text,
          mode: 'local',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'Qwen2-1.5B',
          intent: localResponse.intent,
          slots: localResponse.slots
        }
        this.performanceStats.localRequests++
      }
      
      this.performanceStats.totalResponseTime += (Date.now() - startTime)
      return response
    } catch (error) {
      console.error('Translation failed:', error)
      this.recordCloudError(error as Error)
      
      const localResponse = await localAIService.translateText(text, targetLang)
      return {
        text: localResponse.text,
        mode: 'local',
        cached: false,
        responseTime: Date.now() - startTime,
        modelName: 'Qwen2-1.5B',
        intent: localResponse.intent,
        slots: localResponse.slots
      }
    }
  }

  async classifyText(text: string, categories: string[]): Promise<AIResponse> {
    const startTime = Date.now()
    this.performanceStats.totalRequests++
    
    try {
      const mode = await this.determineMode()
      let response: AIResponse
      
      if (mode === 'cloud' && this.cloudEnabled) {
        const cloudResponse = await deepseekOptimizedService.classifyText?.(text, categories) || 'unknown'
        response = {
          text: cloudResponse,
          mode: 'cloud',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'DeepSeek-V4-Flash'
        }
        this.performanceStats.cloudRequests++
      } else {
        const localResponse = await localAIService.classifyText(text, categories)
        response = {
          text: localResponse.text,
          mode: 'local',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'Qwen2-1.5B',
          intent: localResponse.intent,
          slots: localResponse.slots
        }
        this.performanceStats.localRequests++
      }
      
      this.performanceStats.totalResponseTime += (Date.now() - startTime)
      return response
    } catch (error) {
      console.error('Classification failed:', error)
      this.recordCloudError(error as Error)
      
      const localResponse = await localAIService.classifyText(text, categories)
      return {
        text: localResponse.text,
        mode: 'local',
        cached: false,
        responseTime: Date.now() - startTime,
        modelName: 'Qwen2-1.5B',
        intent: localResponse.intent,
        slots: localResponse.slots
      }
    }
  }

  async extractEntities(text: string): Promise<AIResponse> {
    const startTime = Date.now()
    this.performanceStats.totalRequests++
    
    try {
      const mode = await this.determineMode()
      let response: AIResponse
      
      if (mode === 'cloud' && this.cloudEnabled) {
        const cloudResponse = await deepseekOptimizedService.extractEntities?.(text) || '{}'
        response = {
          text: cloudResponse,
          mode: 'cloud',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'DeepSeek-V4-Flash'
        }
        this.performanceStats.cloudRequests++
      } else {
        const localResponse = await localAIService.extractEntities(text)
        response = {
          text: localResponse.text,
          mode: 'local',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'Qwen2-1.5B',
          intent: localResponse.intent,
          slots: localResponse.slots
        }
        this.performanceStats.localRequests++
      }
      
      this.performanceStats.totalResponseTime += (Date.now() - startTime)
      return response
    } catch (error) {
      console.error('Entity extraction failed:', error)
      this.recordCloudError(error as Error)
      
      const localResponse = await localAIService.extractEntities(text)
      return {
        text: localResponse.text,
        mode: 'local',
        cached: false,
        responseTime: Date.now() - startTime,
        modelName: 'Qwen2-1.5B',
        intent: localResponse.intent,
        slots: localResponse.slots
      }
    }
  }

  async rewriteText(text: string, style: string = 'formal'): Promise<AIResponse> {
    const startTime = Date.now()
    this.performanceStats.totalRequests++
    
    try {
      const mode = await this.determineMode()
      let response: AIResponse
      
      if (mode === 'cloud' && this.cloudEnabled) {
        const cloudResponse = await deepseekOptimizedService.rewriteText?.(text, style) || text
        response = {
          text: cloudResponse,
          mode: 'cloud',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'DeepSeek-V4-Flash'
        }
        this.performanceStats.cloudRequests++
      } else {
        const localResponse = await localAIService.rewriteText(text, style)
        response = {
          text: localResponse.text,
          mode: 'local',
          cached: false,
          responseTime: Date.now() - startTime,
          modelName: 'Qwen2-1.5B',
          intent: localResponse.intent,
          slots: localResponse.slots
        }
        this.performanceStats.localRequests++
      }
      
      this.performanceStats.totalResponseTime += (Date.now() - startTime)
      return response
    } catch (error) {
      console.error('Rewrite failed:', error)
      this.recordCloudError(error as Error)
      
      const localResponse = await localAIService.rewriteText(text, style)
      return {
        text: localResponse.text,
        mode: 'local',
        cached: false,
        responseTime: Date.now() - startTime,
        modelName: 'Qwen2-1.5B',
        intent: localResponse.intent,
        slots: localResponse.slots
      }
    }
  }

  clearHistory(): void {
    localAIService.clearHistory()
  }

  clearCache(): void {
    this.responseCache.clear()
    this.savePersistentCache()
    console.log('Hybrid AI cache cleared')
  }

  getStatus(): ServiceStatus {
    const localStatus = localAIService.getStatus()
    const modelInfo = localAIService.getStats()
    
    return {
      mode: this.currentMode,
      cloud: {
        available: this.cloudEnabled,
        configured: this.cloudEnabled,
        lastSuccess: this.cloudErrorHistory.length === 0 ? new Date() :
          this.cloudErrorHistory.reduce((latest, err) => new Date(Math.max(latest.getTime(), err.timestamp)), new Date(0)),
        errorRate: this.cloudErrorHistory.length / Math.max(this.performanceStats.cloudRequests, 1),
        latency: this.getCloudAvgLatency()
      },
      local: {
        available: true,
        loaded: localStatus.isLoaded,
        loading: localStatus.isLoading,
        modelName: 'Qwen2-1.5B'
      },
      system: {
        activeSessions: localStatus.activeSessions || 0,
        totalRequests: this.performanceStats.totalRequests,
        cacheHitRate: this.performanceStats.totalRequests > 0 
          ? this.performanceStats.cacheHits / this.performanceStats.totalRequests 
          : 0
      }
    }
  }

  getPerformanceStats(): {
    totalRequests: number
    cloudRequests: number
    localRequests: number
    cacheHitRate: number
    avgResponseTime?: number
    errorRate: number
    avgCloudLatency: number
    avgLocalLatency: number
    errorBreakdown: Record<string, number>
  } {
    const total = this.performanceStats
    const cacheHitRate = total.totalRequests > 0 ? total.cacheHits / total.totalRequests : 0
    const avgResponseTime = total.totalRequests > 0 ? total.totalResponseTime / total.totalRequests : undefined
    const errorRate = total.totalRequests > 0 ? total.errors.count / total.totalRequests : 0
    
    return {
      totalRequests: total.totalRequests,
      cloudRequests: total.cloudRequests,
      localRequests: total.localRequests,
      cacheHitRate,
      avgResponseTime,
      errorRate,
      avgCloudLatency: this.getCloudAvgLatency(),
      avgLocalLatency: this.getLocalAvgLatency(),
      errorBreakdown: total.errors.byType
    }
  }

  unload(): void {
    localAIService.unload()
    this.responseCache.clear()
  }
}

export const hybridAIService = new HybridAIService()